import { formatarCnpj, formatarCpf, limparCnpj, validarCnpj, validarCpf } from '@crm/contracts';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth, type Empresa } from '../auth';
import { linkSuporte, Marca, telefoneSuporte } from '../components/Marca';
import { BotaoTour } from '../components/Tour';
import { Erro, Icon } from '../components/ui';

const BENEFICIOS = [
  { cor: 'ouro', icone: 'busca', titulo: 'Prospecção pronta', texto: 'Busque empresas ativas por atividade e cidade, com telefone, e-mail e sócios. Importe para a carteira com um clique.' },
  { cor: 'verde', icone: 'whatsapp', titulo: 'WhatsApp e e-mail no mesmo lugar', texto: 'Mensagens com o nome do cliente, e-mail enviado da caixa do próprio vendedor (com as respostas na ficha) e campanhas para a carteira toda.' },
  { cor: 'azul', icone: 'agenda', titulo: 'Todo contato registrado, retorno no dia certo', texto: 'Registre ligações, visitas e conversas, atualize o status do cliente e agende o próximo retorno. Funil e metas da equipe num painel só.' },
] as const;

export function LoginPage() {
  const { entrar } = useAuth();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { de?: string } };
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const r = await entrar(email, senha);
      if (r.tipo === 'selecionar') nav('/empresas', { state: { empresas: r.empresas, preAuthToken: r.preAuthToken, email } });
      else nav(loc.state?.de ?? '/', { replace: true });
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="login">
      <section className="login-hero" aria-label="Sobre o Garimpa Vendas">
        <Marca tamanho={22} logo={40} />
        <div className="login-pitch">
          <span className="selo">CRM para quem vende pelo WhatsApp e e-mail</span>
          <h1>Garimpe clientes.<br />Feche pelo WhatsApp.</h1>
          <p>Encontre empresas na base oficial da Receita Federal, fale com elas pelo WhatsApp ou e-mail em um clique, registre cada contato e nunca mais perca um retorno.</p>
          <ul className="beneficios">
            {BENEFICIOS.map((b) => (
              <li key={b.titulo}>
                <span className={`ic ${b.cor}`} aria-hidden="true"><Icon name={b.icone} size={20} /></span>
                <div><strong>{b.titulo}</strong><span>{b.texto}</span></div>
              </li>
            ))}
          </ul>
        </div>
        <span className="small faint rodape">Dados abertos da Receita Federal · Origem de cada contato registrada (LGPD) · Opção “não contactar”</span>
      </section>

      <section className="login-lado" aria-label="Entrar">
        <div className="login-col">
      <form className="auth-card" onSubmit={submit}>
        <div>
          <h1 style={{ fontSize: 22 }}>Entrar</h1>
          <p className="muted small" style={{ margin: '6px 0 0' }}>Bem-vindo de volta. Sua carteira está esperando.</p>
        </div>
        <Erro erro={erro} />
        <label className="field"><span>E-mail</span>
          <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} style={{ height: 44 }} />
        </label>
        <label className="field"><span>Senha</span>
          <input className="input" type="password" autoComplete="current-password" required value={senha} onChange={(e) => setSenha(e.target.value)} style={{ height: 44 }} />
        </label>
        <button className="btn primary lg block" disabled={enviando}>{enviando ? 'Entrando…' : 'Entrar'}</button>
        <Link to="/esqueci-senha" className="small" style={{ textAlign: 'center' }}>Esqueci minha senha</Link>
      </form>
      <div className="novo-aqui">
        <strong>Novo por aqui?</strong>
        <span>Em poucos minutos sua empresa está garimpando os primeiros clientes.</span>
        <ol>
          <li><b>1</b>Cadastre a empresa (CNPJ e seu e-mail)</li>
          <li><b>2</b>Garimpe empresas por atividade e cidade</li>
          <li><b>3</b>Faça o primeiro contato por WhatsApp ou e-mail</li>
        </ol>
        <Link to="/cadastro" className="btn lg block ouro-borda">Criar conta da empresa</Link>
        <BotaoTour className="btn block tour-link" />
        <span className="small" style={{ textAlign: 'center', color: '#b8a982' }}>14 dias grátis · sem cartão de crédito</span>
      </div>
      <a className="contato-comercial" href={linkSuporte(undefined, 'Olá! Quero conhecer o Garimpa Vendas.')} target="_blank" rel="noreferrer">
        <span className="ic" aria-hidden="true"><Icon name="whatsapp" size={18} color="#6ee7b7" /></span>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontWeight: 500 }}>Fale com o comercial</span>
          <span className="small faint">WhatsApp {telefoneSuporte(undefined)} · dúvidas e planos</span>
        </span>
      </a>
        </div>
      </section>
    </div>
  );
}

export function EmpresasPage() {
  const { selecionarEmpresa } = useAuth();
  const nav = useNavigate();
  const st = (useLocation().state ?? {}) as { empresas?: Empresa[]; preAuthToken?: string; email?: string };
  const [erro, setErro] = useState<string | null>(null);
  if (!st.empresas || !st.preAuthToken) return <Navigate to="/entrar" replace />;

  async function escolher(id: string) {
    try {
      await selecionarEmpresa(st.preAuthToken!, id);
      nav('/', { replace: true });
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <div className="auth-wrap">
      <Marca />
      <section className="auth-card">
        <div>
          <h1 style={{ fontSize: 22 }}>Escolha a empresa</h1>
          <p className="muted small" style={{ margin: '6px 0 0' }}>{st.email} tem acesso a {st.empresas.length} empresas.</p>
        </div>
        <Erro erro={erro} />
        {st.empresas.map((e) => {
          const bloqueada = e.tenantStatus === 'BLOQUEADO';
          return (
            <button key={e.tenantId} className="org" disabled={bloqueada} onClick={() => escolher(e.tenantId)} style={bloqueada ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}>
              <span className="logo" style={{ width: 38, height: 38 }}>{e.tenantNome.slice(0, 2).toUpperCase()}</span>
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 500 }}>{e.tenantNome}</span>
                <span className="small" style={{ color: bloqueada ? 'var(--red-text)' : 'var(--muted)' }}>
                  {bloqueada ? 'Acesso bloqueado — fale com o suporte' : e.perfil === 'ADMIN' ? 'Administrador(a)' : 'Vendedor(a)'}
                </span>
              </span>
            </button>
          );
        })}
      </section>
    </div>
  );
}

export function CadastroPage() {
  const { cadastrar } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ empresa: '', cnpj: '', nome: '', email: '', senha: '', confirmar: '', aceite: false });
  // conta de empresa (CNPJ) ou de pessoa física (CPF): vendedor que assina para uso próprio (specs/26)
  const [tipo, setTipo] = useState<'CNPJ' | 'CPF'>('CNPJ');
  const cpf = tipo === 'CPF';
  const [erro, setErro] = useState<string | null>(null);
  const [rf, setRf] = useState<{ estado: 'buscando' | 'ok' | 'erro'; cidade?: string | null; uf?: string | null; situacao?: string; msg?: string } | null>(null);
  const ultimo = useRef('');
  // CNPJ completo e válido → busca nome e cidade na base da Receita
  useEffect(() => {
    if (cpf) { setRf(null); ultimo.current = ''; return; }
    const c = limparCnpj(f.cnpj);
    if (c.length !== 14 || !validarCnpj(c)) { if (c.length < 14) { setRf(null); ultimo.current = ''; } return; }
    if (ultimo.current === c) return;
    ultimo.current = c;
    setRf({ estado: 'buscando' });
    api<{ razaoSocial: string; nomeFantasia: string | null; cidade: string | null; uf: string | null; situacao: string }>(`/auth/cnpj/${c}`)
      .then((d) => {
        if (ultimo.current !== c) return;
        setF((v) => ({ ...v, cnpj: formatarCnpj(c), empresa: nomeBonito(d.nomeFantasia || d.razaoSocial) }));
        setRf({ estado: 'ok', cidade: d.cidade ? nomeBonito(d.cidade) : null, uf: d.uf, situacao: d.situacao });
      })
      .catch((e) => { if (ultimo.current === c) setRf({ estado: 'erro', msg: mensagemErro(e) }); });
  }, [f.cnpj, cpf]);
  const [enviando, setEnviando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string; checked?: boolean; type?: string } }) =>
    setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (f.senha !== f.confirmar) return setErro('As senhas não conferem.');
    const doc = f.cnpj.replace(cpf ? /\D/g : /[^0-9A-Za-z]/g, '');
    if (doc && (cpf ? !validarCpf(doc) : !validarCnpj(doc))) return setErro(cpf ? 'CPF inválido. Confira os dígitos.' : 'CNPJ inválido. Confira os dígitos.');
    setEnviando(true);
    try {
      const local = !cpf && rf?.estado === 'ok' ? { cidade: rf.cidade || undefined, estado: rf.uf || undefined } : {};
      // pessoa física sem nome de conta: usa o próprio nome
      const empresa = f.empresa.trim() || (cpf ? f.nome.trim() : '');
      await cadastrar({ empresa, documento: doc || undefined, ...local, nome: f.nome, email: f.email, senha: f.senha, aceiteTermos: f.aceite });
      nav('/primeiros-passos', { replace: true });
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-wrap">
      <Marca />
      <form className="auth-card" onSubmit={submit} style={{ maxWidth: 520 }}>
        <div>
          <h1 style={{ fontSize: 22 }}>{cpf ? 'Criar sua conta' : 'Criar conta da empresa'}</h1>
          <p className="muted small" style={{ margin: '6px 0 0' }}><b style={{ color: '#fde68a', fontWeight: 600 }}>14 dias grátis, sem cartão de crédito.</b> Leva menos de 2 minutos e você entra como administrador(a).</p>
        </div>
        <Erro erro={erro} />
        <div className="form-grid">
          <div className="field span-2">
            <span>A conta é de</span>
            <div className="seg" role="group" aria-label="Tipo de conta">
              <button type="button" aria-pressed={!cpf} onClick={() => { setTipo('CNPJ'); setF((v) => ({ ...v, cnpj: '' })); }}>Empresa (CNPJ)</button>
              <button type="button" aria-pressed={cpf} onClick={() => { setTipo('CPF'); setF((v) => ({ ...v, cnpj: '' })); }}>Uso próprio (CPF)</button>
            </div>
          </div>
          {cpf ? (
            <label className="field"><span>CPF</span>
              <input className="input num" inputMode="numeric" value={f.cnpj} maxLength={14} placeholder="000.000.000-00" aria-describedby="cpf-hint"
                onChange={set('cnpj')} onBlur={() => { const c = f.cnpj.replace(/\D/g, ''); if (validarCpf(c)) setF((v) => ({ ...v, cnpj: formatarCpf(c) })); }} />
              <span id="cpf-hint" className="hint">Opcional. Para vendedor que assina por conta própria.</span>
            </label>
          ) : (
            <label className="field"><span>CNPJ</span>
              <input className="input num" inputMode="numeric" value={f.cnpj} onChange={set('cnpj')} placeholder="00.000.000/0000-00" aria-describedby="cnpj-hint" />
              <span id="cnpj-hint" className="hint" aria-live="polite" style={rf?.estado === 'ok' ? { color: 'var(--green-text)' } : undefined}>
                {rf?.estado === 'buscando' ? 'Buscando na Receita Federal…'
                  : rf?.estado === 'ok' ? `✓ ${[rf.cidade, rf.uf].filter(Boolean).join('/')}${rf.situacao && rf.situacao !== 'ATIVA' ? ` · situação ${rf.situacao.toLowerCase()}` : ''}`
                  : rf?.estado === 'erro' ? rf.msg
                  : 'Preenchemos o nome e a cidade para você.'}
              </span>
            </label>
          )}
          {cpf ? (
            <label className="field"><span>Nome da conta</span>
              <input className="input" value={f.empresa} onChange={set('empresa')} placeholder={f.nome.trim() || 'Ex.: Ana Ribeiro Representações'} />
              <span className="hint">Aparece no menu. Em branco, usamos o seu nome.</span>
            </label>
          ) : (
            <label className="field"><span>Nome da empresa *</span><input className="input" required value={f.empresa} onChange={set('empresa')} /></label>
          )}
          <label className="field"><span>Seu nome *</span><input className="input" required value={f.nome} onChange={set('nome')} /></label>
          <label className="field"><span>E-mail *</span><input className="input" type="email" required value={f.email} onChange={set('email')} autoComplete="username" /></label>
          <label className="field"><span>Senha *</span><input className="input" type="password" required minLength={10} value={f.senha} onChange={set('senha')} autoComplete="new-password" /><span className="hint">Mínimo de 10 caracteres.</span></label>
          <label className="field"><span>Confirmar senha *</span><input className="input" type="password" required value={f.confirmar} onChange={set('confirmar')} autoComplete="new-password" /></label>
        </div>
        <label className="check"><input type="checkbox" checked={f.aceite} onChange={set('aceite')} required />Li e aceito os termos de uso e a política de privacidade.</label>
        <button className="btn primary lg block" disabled={enviando}>{enviando ? 'Criando…' : 'Criar conta'}</button>
        <span className="muted small" style={{ textAlign: 'center' }}>Já tem conta? <Link to="/entrar">Entrar</Link></span>
      </form>
    </div>
  );
}

export function EsqueciSenhaPage() {
  const [email, setEmail] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await api('/auth/esqueci-senha', { body: { email } });
      setEnviado(true);
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-wrap">
      <Marca />
      <form className="auth-card" onSubmit={submit}>
        <div>
          <h1 style={{ fontSize: 22 }}>Esqueci minha senha</h1>
          <p className="muted small" style={{ margin: '6px 0 0' }}>Informe seu e-mail e enviaremos um link para criar uma nova senha.</p>
        </div>
        {enviado ? (
          <div className="alert info"><Icon name="check" /><span>Se <b>{email}</b> tiver conta, você vai receber o link em instantes. Ele vale por 1 hora. Confira também a caixa de spam.</span></div>
        ) : (
          <>
            <Erro erro={erro} />
            <label className="field"><span>E-mail</span>
              <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} style={{ height: 44 }} autoFocus />
            </label>
            <button className="btn primary lg block" disabled={enviando}>{enviando ? 'Enviando…' : 'Enviar link'}</button>
          </>
        )}
        <div className="sep" style={{ margin: 0 }} />
        <span className="muted small" style={{ textAlign: 'center' }}><Link to="/entrar">Voltar para o login</Link></span>
      </form>
    </div>
  );
}

export function RedefinirSenhaPage() {
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.hash.slice(1)).get('t') ?? '';
  const [f, setF] = useState({ novaSenha: '', confirmar: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [feito, setFeito] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (f.novaSenha !== f.confirmar) return setErro('A confirmação não confere com a nova senha.');
    setEnviando(true);
    try {
      await api('/auth/redefinir-senha', { body: { token, novaSenha: f.novaSenha } });
      window.history.replaceState(null, '', window.location.pathname); // tira o token da barra de endereço
      setFeito(true);
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-wrap">
      <Marca />
      <form className="auth-card" onSubmit={submit}>
        <h1 style={{ fontSize: 22 }}>Criar nova senha</h1>
        {feito ? (
          <>
            <div className="alert info"><Icon name="check" /><span>Senha alterada. Entre com a nova senha.</span></div>
            <button type="button" className="btn primary lg block" onClick={() => nav('/entrar', { replace: true })}>Ir para o login</button>
          </>
        ) : !token ? (
          <>
            <Erro erro="Link inválido. Peça um novo em “Esqueci minha senha”." />
            <Link to="/esqueci-senha" className="btn lg block">Pedir novo link</Link>
          </>
        ) : (
          <>
            <Erro erro={erro} />
            <label className="field"><span>Nova senha</span>
              <input className="input" type="password" autoComplete="new-password" required minLength={10} value={f.novaSenha} onChange={(e) => setF({ ...f, novaSenha: e.target.value })} style={{ height: 44 }} autoFocus />
              <span className="hint">Mínimo de 10 caracteres.</span>
            </label>
            <label className="field"><span>Confirmar nova senha</span>
              <input className="input" type="password" autoComplete="new-password" required value={f.confirmar} onChange={(e) => setF({ ...f, confirmar: e.target.value })} style={{ height: 44 }} />
            </label>
            <button className="btn primary lg block" disabled={enviando}>{enviando ? 'Salvando…' : 'Salvar nova senha'}</button>
          </>
        )}
      </form>
    </div>
  );
}

/** "MERCADO BOM PRECO LTDA" → "Mercado Bom Preco Ltda" (base da Receita vem em maiúsculas). */
const MINUSCULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
function nomeBonito(t: string) {
  if (t !== t.toUpperCase()) return t.trim();
  return t.toLowerCase().trim().split(/\s+/)
    .map((w, i) => (i > 0 && MINUSCULAS.has(w) ? w : /^(me|epp|s\/a|sa|eireli)$/.test(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}
